import { useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { Container, Lines } from '@/components/ui'
import { CmsImage } from '@/components/media/CmsImage'
import { cmsHeroSlides } from '@/content/cms/hero-slides.generated'
import { cmsMediaLargestWebpUrl } from '@/utils/cmsMediaUrl'
import { LEGACY_HERO_SLIDES } from '@/content/heroLegacy'
import { heroGradientClass, heroImagePositionClass } from '@/content/heroTokens'
import { buildLocalePath, type Locale } from '@/utils/localePath'
import { resolveHeroCta } from '@/content/heroCta'

/**
 * The homepage hero carousel.
 *
 * WHERE THE SLIDES COME FROM
 * --------------------------
 * The slides are CMS rows (`cms_hero_slides`), snapshotted at build time
 * into `cmsHeroSlides` by `npm run cms:sync`. The operator adds, edits,
 * deletes, hides and reorders them in `/admin/cms/pages/home`.
 *
 * The snapshot carries TOKENS for the gradient and the photo position, not
 * Tailwind classes — see `src/content/heroTokens.ts` for why a class string
 * in a database row renders as no gradient at all.
 *
 * THE COMPILED-IN FALLBACK
 * ------------------------
 * When the snapshot is EMPTY (no CMS at build time, or a fresh install with
 * an empty table) the carousel renders the six slides that shipped with the
 * site, from `src/content/heroLegacy.ts`. The homepage must never open on a
 * blank hero.
 *
 * Note the distinction this preserves: a snapshot that is empty means "the
 * CMS has no slides"; a snapshot whose slides are all HIDDEN means "the
 * operator chose that". The second one renders an empty hero — deliberately
 * — rather than silently resurrecting the old slides.
 */

/**
 * One slide, normalised for rendering.
 *
 * The CMS path and the compiled-in path carry their photo differently — a
 * media-library UUID versus a plain `public/` path — so both are folded into
 * one shape here and the markup below does not care which it is looking at.
 */
interface HeroSlideView {
  key: string
  tag: string
  title1: string
  title2: string
  text: string
  cta: string
  ctaLink: string
  /** The media-library UUID of the photo, when the slide has one. */
  imageUuid: string | null
  /** A plain `public/` path, for the compiled-in fallback slides. */
  imageUrl: string | null
  imageAlt: string
  imagePositionClass: string
  gradientClass: string
  icon: string
}

const EMPTY_COPY = {
  tag: '',
  title1: '',
  title2: '',
  text: '',
  cta: '',
  imageAlt: '',
}

export default function HeroSlider(_props: { settings?: { slides?: number } } = {}) {
  const { t, i18n } = useTranslation()
  // Every public URL carries a locale prefix (/zh-Hant, /zh-Hans, /en).
  // A bare `/products/smart-cindy` link would be bounced by the bare-path
  // redirect map into the DEFAULT locale, so a visitor on /en/ would be
  // thrown back into Chinese. Build the prefixed path.
  const locale = (i18n.language || 'zh-Hant') as Locale

  /**
   * The slides to render, in order.
   *
   * Not memoised: the list is six-ish items and the carousel re-renders on
   * every scroll anyway, so a cache would buy nothing and would need a
   * dependency list that is easy to get wrong.
   */
  const slides: HeroSlideView[] =
    cmsHeroSlides.length > 0
      ? cmsHeroSlides
          .filter((s) => s.isVisible)
          .map((s) => {
            // The server resolved every locale itself (it is the only place
            // that can convert Traditional to Simplified), so the entry for
            // the active locale is normally present. The fallbacks are for
            // a snapshot written by an older sync.
            const copy = s.copy[locale] ?? s.copy['zh-Hant'] ?? s.copy.en ?? EMPTY_COPY
            return {
              key: `slide-${s.id}`,
              tag: copy.tag,
              title1: copy.title1,
              title2: copy.title2,
              text: copy.text,
              cta: copy.cta,
              ctaLink: s.ctaLink,
              imageUuid: s.imageMediaUuid,
              imageUrl: null,
              imageAlt: copy.imageAlt,
              imagePositionClass: heroImagePositionClass(s.imagePosition),
              gradientClass: heroGradientClass(s.gradientToken),
              icon: s.icon,
            }
          })
      : // The compiled-in fallback. Its copy still lives in the i18n bundles
        // under `home.hero.slide<n>.*`, because the CMS rows do not exist
        // yet — there is nothing else to read it from.
        LEGACY_HERO_SLIDES.map((l) => ({
          key: `legacy-slide-${l.index}`,
          tag: t(`${l.copyKeyBase}.tag`),
          title1: t(`${l.copyKeyBase}.title1`),
          title2: t(`${l.copyKeyBase}.title2`),
          text: t(`${l.copyKeyBase}.text`),
          cta: t(`${l.copyKeyBase}.cta`),
          ctaLink: l.ctaLink,
          imageUuid: null,
          imageUrl: l.image,
          imageAlt: l.imageAlt,
          imagePositionClass: heroImagePositionClass(l.imagePosition),
          gradientClass: heroGradientClass(l.gradient),
          icon: l.icon,
        }))

  const slideCount = slides.length

  // Embla caches its scroll snaps from the DOM it was created against, so it
  // has to be told to re-measure when the slide count changes. That can
  // happen now: the operator adds or deletes a slide in the CMS and the next
  // build has a different number of them.
  //
  // (embla-carousel-react v8's hook takes no dependency array — it only
  // re-initialises itself when the OPTIONS object changes — so the re-init
  // is explicit here.)
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 30 })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [autoplay, setAutoplay] = useState(true)

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.reInit()
  }, [emblaApi, slideCount])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
  }, [emblaApi, onSelect])

  // Autoplay
  useEffect(() => {
    if (!emblaApi || !autoplay || slideCount === 0) return
    const id = setInterval(() => emblaApi.scrollNext(), 6000)
    return () => clearInterval(id)
  }, [emblaApi, autoplay, slideCount])

  const scrollTo = useCallback(
    (i: number) => emblaApi && emblaApi.scrollTo(i),
    [emblaApi],
  )

  // Every slide is hidden. The operator chose that, so the carousel renders
  // nothing at all — an empty hero rather than the old slides coming back
  // from the dead.
  if (slideCount === 0) return null

  return (
    <section
      className="relative bg-gradient-to-br from-surface via-surface-2 to-surface overflow-hidden"
      aria-label="Hero"
      onMouseEnter={() => setAutoplay(false)}
      onMouseLeave={() => setAutoplay(true)}
    >
      {/* Decorative sparkles */}
      <div aria-hidden="true" className="absolute top-12 right-8 md:right-16 w-20 md:w-32 text-accent opacity-40 pointer-events-none">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10Z" />
        </svg>
      </div>
      <div aria-hidden="true" className="absolute bottom-20 left-8 md:left-24 w-12 md:w-20 text-ink-soft opacity-30 pointer-events-none">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10Z" />
        </svg>
      </div>

      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex">
          {slides.map((slide, idx) => {
            // Whether this slide actually shows a photo. The CMS path goes
            // through the media index (a UUID that is not in it means the
            // file was never uploaded, or the media sync has not run yet);
            // the compiled-in path points straight at a `public/` file.
            //
            // Deciding this HERE — rather than letting <CmsImage> render
            // nothing on its own — is what keeps the icon fallback honest:
            // a slide whose photo cannot be resolved shows its icon tile,
            // exactly like a slide that was never given a photo.
            const hasPhoto = slide.imageUuid
              ? Boolean(cmsMediaLargestWebpUrl(slide.imageUuid))
              : Boolean(slide.imageUrl)

            // Where the slide's OWN CTA points.
            //
            // The value comes from the CMS, so it is untrusted input, and it
            // can be EITHER of two shapes:
            //
            //   - a root-relative path (`/products/values-incubator`), which
            //     the renderer prefixes with the active locale — so a visitor
            //     on /en/ is not bounced back into Chinese by the bare-path
            //     redirect map;
            //   - an absolute http(s) URL (CSP lives at https://csp.yada.hk,
            //     not under www.yada.hk), which must become a PLAIN `<a>`:
            //     `buildLocalePath()` would turn it into the nonsense path
            //     `/zh-Hanthttps://csp.yada.hk`, and a React Router `<Link>`
            //     would try to route it inside the SPA.
            //
            // WHICH of the two it is — and whether it is safe at all — is
            // decided ONCE, in `src/content/heroCta.ts` (`resolveHeroCta`),
            // by the site's EXISTING URL policies: `safeInternalLink` for the
            // path branch (a root-relative path, returned AS-IS) and
            // `safeExternalUrl` for the external branch (http(s) only).
            //
            // Those are the very policies the SERVER's write schema uses for
            // this column (`server/routes/hero-slides.ts`, `ctaLinkSchema`),
            // so the value the admin form accepts and the value the renderer
            // treats as "external" are decided by the SAME rule on both
            // sides, and they cannot drift apart.
            // `scripts/test-hero-slides.mts` asserts that agreement vector by
            // vector.
            //
            // A value that is neither (a `javascript:` URL, a `data:` URL, a
            // bare host, a protocol-relative `//evil.example`, a relative
            // path with no leading slash) yields NO href at all — the CTA
            // renders as plain text. Nothing untrusted ever reaches an
            // `href` or a router `to`.
            const cta = resolveHeroCta(slide.ctaLink)

            return (
            <div
              key={slide.key}
              className="flex-[0_0_100%] min-w-0"
              role="group"
              aria-roledescription="slide"
              aria-label={`Slide ${idx + 1} of ${slideCount}`}
            >
              <div className="py-section-y md:py-12 px-section-x">
                <Container>
                  <div className="grid lg:grid-cols-[minmax(0,1fr)_var(--hero-visual-size)] gap-6 lg:gap-12 items-center">
                    {/* Content — Phase 1 mobile (2026-10-13):
                        - order classes removed: text now ALWAYS comes before
                        the visual (the previous `order-2` on mobile put a
                        400×400 colour card above the H1, hiding the
                        headline below the fold).
                        - mb-6 → mb-4 md:mb-6 so the H1 doesn't push the
                        subtitle below the fold on small phones.
                        - desktop section padding dropped from md:py-24 (96px)
                        to md:py-12 (48px) and lg:gap-16 → lg:gap-12 to stop
                        the visual card floating in a sea of empty space
                        when the content column is shorter than 96px of
                        breathing room. The aspect-square card now
                        dominates the slide instead of orbiting the middle. */}
                    <div>
                      <div className="inline-flex items-center gap-2 bg-accent/20 text-ink px-3 py-1 md:px-4 md:py-1.5 rounded-yada-full mb-3 md:mb-6">
                        <Icon icon={slide.icon} className="text-sm md:text-base" />
                        <span className="text-xs md:text-sm font-semibold">
                          {slide.tag}
                        </span>
                      </div>
                      <h1 className="hero-title text-[1.7rem] sm:text-3xl md:text-4xl lg:text-5xl text-ink mb-4 md:mb-6 leading-[1.2] md:leading-[1.1]">
                        <span className="block">
                          {slide.title1}
                        </span>
                        <span className="block text-primary mt-1 md:mt-0">
                          {slide.title2}
                        </span>
                      </h1>
                      <p className="text-base md:text-xl text-ink-soft mb-6 md:mb-8 max-w-2xl leading-relaxed">
                        {slide.text}
                      </p>
                      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 w-full sm:w-auto">
                        {/* The slide's OWN CTA. Two branches, decided by the
                            URL policies above:
                              - internal → a React Router <Link>, so the
                                locale prefix is applied and the SPA routes
                                it;
                              - external → a plain <a target="_blank">, because
                                the destination is another site (CSP lives at
                                csp.yada.hk) and `buildLocalePath()` would
                                mangle it into `/zh-Hanthttps://csp.yada.hk`.
                            The button styling is identical in both, so the
                            slide looks the same either way. */}
                        {cta.kind === 'internal' ? (
                          <Link
                            to={buildLocalePath(cta.href, locale)}
                            className="inline-flex items-center justify-center gap-2 bg-primary text-surface px-5 py-3 md:px-8 md:py-4 rounded-yada-full hover:bg-primary-dark transition-all hover:-translate-y-0.5 text-sm md:text-base font-semibold shadow-yada-md w-full sm:w-auto"
                          >
                            <span>{slide.cta}</span>
                            <Icon icon="lucide:arrow-right" />
                          </Link>
                        ) : cta.kind === 'external' ? (
                          <a
                            href={cta.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 bg-primary text-surface px-5 py-3 md:px-8 md:py-4 rounded-yada-full hover:bg-primary-dark transition-all hover:-translate-y-0.5 text-sm md:text-base font-semibold shadow-yada-md w-full sm:w-auto"
                          >
                            <span>{slide.cta}</span>
                            <Icon icon="lucide:arrow-right" />
                          </a>
                        ) : (
                          /* Neither shape survived validation — the value is
                             refused by the URL policy, so it is shown as
                             plain text rather than as a link that goes
                             nowhere (or, worse, somewhere untrusted). */
                          <span className="inline-flex items-center justify-center gap-2 bg-primary text-surface px-5 py-3 md:px-8 md:py-4 rounded-yada-full text-sm md:text-base font-semibold shadow-yada-md w-full sm:w-auto">
                            <span>{slide.cta}</span>
                            <Icon icon="lucide:arrow-right" />
                          </span>
                        )}
                        <Link
                          to={buildLocalePath('/contact', locale)}
                          className="inline-flex items-center justify-center gap-2 bg-transparent text-ink border-2 border-ink px-5 py-3 md:px-8 md:py-4 rounded-yada-full hover:bg-ink hover:text-surface transition-all text-sm md:text-base font-semibold w-full sm:w-auto"
                        >
                          <Lines text={t('common.getInTouch')} />
                        </Link>
                      </div>
                    </div>

                    {/* Visual — a flat square, capped by --hero-visual-size (240px) at every
                        breakpoint. Kept square by giving `max-w` and `max-h`
                        the SAME value: `aspect-square` derives height from
                        width, so a max-h lower than max-w would squash the
                        ratio (the earlier md:max-w-md + md:max-h-[400px]
                        pair rendered 448x400).

                        The grid template on the wrapper is the bigger lever
                        than the px value: `lg:grid-cols-[minmax(0,1fr)_var(--hero-visual-size)]`
                        stops the image column claiming 50% of the row and
                        hands that width back to the copy column (measured
                        552px -> 888px of copy at a 1200px container, i.e.
                        3 wrapped lines instead of 4).

                        The second track MUST be a definite length, NOT `auto`.
                        An `auto` track sizes from the grid item's intrinsic
                        width, and this card contains only absolutely-
                        positioned children — so it has no in-flow content
                        to measure and the track collapses. Measured in
                        Chrome: `auto` resolved the track to `0px` and the
                        card rendered 0x0, i.e. no image at all. Adding
                        `lg:w-auto` to the wrapper does NOT rescue it,
                        because a `width:auto` flex container is still
                        shrink-to-fit around zero in-flow content.
                        Hence the CSS var, which the track and the card's
                        own caps all read, so the two cannot drift apart. */}
                    <div className="flex items-center justify-center mt-4 lg:mt-0">
                      <div
                        className={`relative w-full max-w-[var(--hero-visual-size)] aspect-square max-h-[var(--hero-visual-size)] rounded-yada-2xl bg-gradient-to-br ${slide.gradientClass} flex items-center justify-center shadow-yada-xl overflow-hidden`}
                      >
                        {/* Background image (when provided) */}
                        {slide.imageUuid ? (
                          <CmsImage
                            uuid={slide.imageUuid}
                            alt={slide.imageAlt}
                            // The card is capped by --hero-visual-size: 280px
                            // below `lg`, 240px from `lg` up. Both numbers
                            // are read straight out of the CSS, so this is a
                            // measurement rather than a guess — the browser
                            // picks the smallest rung that still covers it.
                            sizes="(min-width: 1024px) 240px, 280px"
                            className={`absolute inset-0 w-full h-full object-cover ${slide.imagePositionClass}`}
                            loading={idx === 0 ? 'eager' : 'lazy'}
                            priority={idx === 0}
                          />
                        ) : slide.imageUrl ? (
                          <img
                            src={slide.imageUrl}
                            alt={slide.imageAlt}
                            className={`absolute inset-0 w-full h-full object-cover ${slide.imagePositionClass}`}
                            loading={idx === 0 ? 'eager' : 'lazy'}
                            decoding="async"
                            // The first slide is above the fold and is the
                            // page's LCP element: it gets a high fetch
                            // priority. The rest stay lazy — they are off
                            // screen, and an eager image below the fold only
                            // steals bandwidth from the one that matters.
                            fetchPriority={idx === 0 ? 'high' : undefined}
                          />
                        ) : null}

                        {/* Decorative shapes */}
                        <div className="absolute -top-8 -right-8 w-32 h-32 bg-accent/30 rounded-full blur-2xl" />
                        <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-surface/30 rounded-full blur-2xl" />

                        {/* Subtle gradient veil so the image sits well inside the card */}
                        {hasPhoto && (
                          <div className="absolute inset-0 bg-gradient-to-tr from-black/15 via-transparent to-transparent pointer-events-none" />
                        )}

                        {/* Icon — only when no image is set (fallback visual) */}
                        {!hasPhoto && (
                          <Icon
                            icon={slide.icon}
                            className="text-surface relative z-10"
                            style={{ fontSize: '8rem' }}
                          />
                        )}

                        {/* Floating sparkles */}
                        <div className="absolute top-8 left-8 w-4 h-4 bg-accent rounded-full animate-pulse" />
                        <div className="absolute bottom-16 right-12 w-3 h-3 bg-surface rounded-full animate-pulse" />
                        <div className="absolute top-1/3 right-8 w-2 h-2 bg-accent rounded-full animate-pulse" />
                      </div>
                    </div>
                  </div>
                </Container>
              </div>
            </div>
            )
          })}
        </div>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 pb-6 md:pb-8" role="tablist" aria-label="Slides">
        {slides.map((slide, idx) => (
          <button
            key={slide.key}
            role="tab"
            aria-selected={selectedIndex === idx}
            aria-label={`Go to slide ${idx + 1}`}
            onClick={() => scrollTo(idx)}
            className={`h-2 rounded-full transition-all ${
              selectedIndex === idx
                ? 'w-10 bg-primary'
                : 'w-2 bg-ink-soft/30 hover:bg-ink-soft/50'
            }`}
          />
        ))}
      </div>
    </section>
  )
}
